// src/pages/Invoice-page/Invoice.js
import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  MdSearch,
  MdClear,
  MdRefresh,
  MdVisibility,
  MdFileDownload,
  MdFilterList,
  MdReceipt
} from 'react-icons/md';
import {
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiOutlineDocumentText,
  HiOutlineCurrencyRupee,
  HiOutlineUserGroup
} from 'react-icons/hi';
import { FaFileInvoice, FaTimes, FaPrint, FaBuilding, FaBoxes } from 'react-icons/fa';
import BASE_URL from '../../api';
import generateInvoicePDF, { cleanClientAddress } from '../../utils/generateInvoicePDF';

const API_BASE = `${BASE_URL}/api`;

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
    borderRadius: '0.5rem',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
    '&:hover': { borderColor: '#2563eb' },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// Format client address using cleanClientAddress
const formatClientAddress = cleanClientAddress;

const Invoice = () => {
  const token = localStorage.getItem('token');
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  // View state
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  // Modals
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);

  const [formData, setFormData] = useState({
    fromDate: '',
    toDate: '',
    clientId: ''
  });

  // ────── FETCH CLIENTS ──────
  const fetchClients = async () => {
    try {
      const res = await fetch(`${API_BASE}/master/customer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) setClients(json.data || []);
    } catch (err) {
      console.error('Client fetch error:', err);
    }
  };

  // ────── FETCH INVOICES ──────
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/getctrl/invoice`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) setInvoices(json.data || []);
    } catch (err) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchClients();
      fetchInvoices();
    }
  }, [token]);

  // ────── GENERATE INVOICE ──────
  const handleGenerate = async () => {
    if (!formData.fromDate || !formData.toDate || !formData.clientId) {
      return toast.error('All fields are required');
    }
    try {
      const res = await fetch(`${API_BASE}/getctrl/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Invoice generated successfully!');
        setShowGenerateForm(false);
        setFormData({ fromDate: '', toDate: '', clientId: '' });
        fetchInvoices();
      } else {
        toast.error(json.message || 'Failed to generate invoice');
      }
    } catch (err) {
      toast.error('Network error during invoice generation');
    }
  };

  // ────── PROFESSIONAL TAX INVOICE PDF GENERATION (A4) ──────
  const handlePrint = (inv) => {
    generateInvoicePDF(inv, token);
  };

  // ────── SEARCH & FILTER ──────
  const filtered = useMemo(() => {
    const s = searchTerm.toLowerCase().trim();
    return invoices.filter(inv => {
      const clientName = inv.clientId?.name || '';
      const companyName = inv.clientId?.companyName || '';
      const invNo = inv.invoiceNumber || '';
      const billMonth = inv.billMonth || '';

      const matchesSearch =
        !s ||
        invNo.toLowerCase().includes(s) ||
        clientName.toLowerCase().includes(s) ||
        companyName.toLowerCase().includes(s) ||
        billMonth.toLowerCase().includes(s);

      const matchesClient = !filterClient || inv.clientId?._id === filterClient;

      return matchesSearch && matchesClient;
    });
  }, [invoices, searchTerm, filterClient]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const resetFilters = () => {
    setSearchTerm('');
    setFilterClient('');
    setCurrentPage(1);
  };

  // KPI Calculations
  const totalInvoices = invoices.length;
  const totalBilledAmount = invoices.reduce((sum, inv) => sum + (inv.grandTotal || 0), 0);
  const invoicedClientsCount = new Set(invoices.map(i => i.clientId?._id).filter(Boolean)).size;
  const totalConsignmentsBilled = invoices.reduce((sum, inv) => sum + (Number(inv.noOfConsignment) || 0), 0);

  // ────── EXCEL EXPORT ──────
  const exportToExcel = () => {
    if (invoices.length === 0) {
      toast.warn('No invoices to export');
      return;
    }

    const exportData = filtered.map((inv, i) => ({
      'S.No': i + 1,
      'Invoice No': inv.invoiceNumber,
      'Company Name': inv.clientId?.companyName || 'N/A',
      'Client Name': inv.clientId?.name || 'N/A',
      'Billing Month': inv.billMonth || 'N/A',
      'Consignments': inv.noOfConsignment || 0,
      'Freight Charge (₹)': inv.frieghtCharge || 0,
      'CGST (%)': inv.cgst || 0,
      'SGST (%)': inv.sgst || 0,
      'Grand Total (₹)': inv.grandTotal || 0,
      'Invoice Date': inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString('en-GB') : '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');
    XLSX.writeFile(workbook, `Invoices_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Invoices exported to Excel!');
  };

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Page Title & Breadcrumb Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
            <MdReceipt className="text-blue-600 text-3xl" />
            Invoice Master
          </h2>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            Generate client billing statements, track consignment invoices, and print tax invoices
          </p>
        </div>

        <button
          onClick={() => setShowGenerateForm(true)}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow transition-all duration-150 flex items-center gap-2 text-sm uppercase tracking-wide cursor-pointer w-fit"
        >
          <FaFileInvoice /> Generate Invoice
        </button>
      </div>

      {/* KPI Summary Quick Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-2xl font-bold">
            <HiOutlineDocumentText />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Invoices</p>
            <p className="text-2xl font-extrabold text-gray-900">{totalInvoices}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center text-2xl font-bold">
            <HiOutlineCurrencyRupee />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Billed</p>
            <p className="text-2xl font-extrabold text-emerald-600">
              ₹{totalBilledAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center text-2xl font-bold">
            <HiOutlineUserGroup />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Billed Clients</p>
            <p className="text-2xl font-extrabold text-purple-600">{invoicedClientsCount}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center text-2xl font-bold">
            <FaBoxes />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Consignments</p>
            <p className="text-2xl font-extrabold text-indigo-600">{totalConsignmentsBilled}</p>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5 md:p-6">
        {/* Unified Control Toolbar */}
        <div className="flex flex-col gap-4 mb-6 pb-5 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Search Input */}
            <div className="relative flex-1 max-w-lg">
              <MdSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
              <input
                type="text"
                placeholder="Search by invoice no, client or company..."
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <MdClear size={18} />
                </button>
              )}
            </div>

            {/* View Switcher, Entries Per Page & Excel Export */}
            <div className="flex items-center flex-wrap gap-2.5">
              {/* Entries per page */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500">Show:</span>
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="py-2 px-2.5 bg-gray-50 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={6}>6 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200">
                <button
                  type="button"
                  onClick={() => setViewMode('card')}
                  className={`p-1.5 rounded-md flex items-center gap-1.5 text-xs font-medium transition cursor-pointer ${
                    viewMode === 'card'
                      ? 'bg-white text-blue-600 shadow-sm font-bold'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Landscape Card View"
                >
                  <HiOutlineViewGrid size={18} />
                  <span className="hidden sm:inline">Cards</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-md flex items-center gap-1.5 text-xs font-medium transition cursor-pointer ${
                    viewMode === 'table'
                      ? 'bg-white text-blue-600 shadow-sm font-bold'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Table List View"
                >
                  <HiOutlineViewList size={18} />
                  <span className="hidden sm:inline">Table</span>
                </button>
              </div>

              {/* Export Excel Button */}
              <button
                onClick={exportToExcel}
                className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm transition cursor-pointer"
                title="Export Invoices to Excel"
              >
                <MdFileDownload size={18} />
                <span className="hidden sm:inline">Export Excel</span>
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <span className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
              <MdFilterList /> Filters:
            </span>

            {/* Filter by Client */}
            <select
              value={filterClient}
              onChange={e => {
                setFilterClient(e.target.value);
                setCurrentPage(1);
              }}
              className="py-1.5 px-3 bg-gray-50 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[220px]"
            >
              <option value="">All Clients</option>
              {clients.map(c => (
                <option key={c._id} value={c._id}>
                  {c.companyName || c.name}
                </option>
              ))}
            </select>

            {(searchTerm || filterClient) && (
              <button
                onClick={resetFilters}
                className="py-1.5 px-3 text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer"
              >
                <MdRefresh /> Reset Filters
              </button>
            )}
          </div>
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="text-center py-16 text-gray-500">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent mb-3"></div>
            <p className="text-sm font-medium">Loading invoices...</p>
          </div>
        ) : paginated.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <HiOutlineDocumentText className="mx-auto text-5xl text-gray-300 mb-2" />
            <p className="text-gray-700 font-bold text-base">No invoices found</p>
            <p className="text-gray-500 text-xs mt-1">Try adjusting your search criteria or generate a new invoice.</p>
            <button
              onClick={() => setShowGenerateForm(true)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition"
            >
              Generate First Invoice
            </button>
          </div>
        ) : viewMode === 'card' ? (
          /* LANDSCAPE CARD VIEW */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-5">
            {paginated.map(inv => {
              const client = inv.clientId || {};
              const company = client.companyName || 'N/A';
              const name = client.name || '';

              return (
                <div
                  key={inv._id}
                  className="bg-white rounded-xl border border-gray-200 hover:border-blue-400 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50/40 px-5 py-3.5 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-sm shrink-0">
                        <MdReceipt size={20} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-extrabold text-blue-700 truncate tracking-wide">
                          {inv.invoiceNumber}
                        </h3>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded uppercase">
                            {inv.billMonth || 'PERIOD'}
                          </span>
                          <span className="text-[10px] text-gray-400">
                            {inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString('en-GB') : ''}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => setViewInvoice(inv)}
                        className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-white rounded-lg transition"
                        title="View Invoice Details"
                      >
                        <MdVisibility size={18} />
                      </button>
                      <button
                        onClick={() => handlePrint(inv)}
                        className="p-1.5 text-emerald-600 hover:text-emerald-800 hover:bg-white rounded-lg transition"
                        title="Print Tax Invoice (PDF)"
                      >
                        <FaPrint size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 space-y-4 flex-1">
                    {/* Client & Company */}
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <p className="text-[10px] font-bold uppercase text-gray-400 flex items-center gap-1">
                        <FaBuilding /> Client / Bill To
                      </p>
                      <p className="text-sm font-bold text-gray-900 truncate mt-0.5">
                        {company} {name ? `(${name})` : ''}
                      </p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        GSTIN: <span className="font-semibold text-gray-700">{client.gstNumber || 'N/A'}</span>
                        {client.station ? ` • Station: ${client.station}` : ''}
                      </p>
                    </div>

                    {/* Financial Metrics */}
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="bg-blue-50 p-2.5 rounded-lg border border-blue-100">
                        <p className="text-[10px] uppercase font-bold text-blue-700">Consignments</p>
                        <p className="text-sm font-extrabold text-blue-800 mt-0.5">
                          {inv.noOfConsignment || 0}
                        </p>
                      </div>
                      <div className="bg-purple-50 p-2.5 rounded-lg border border-purple-100">
                        <p className="text-[10px] uppercase font-bold text-purple-700">Tax Total</p>
                        <p className="text-sm font-extrabold text-purple-800 mt-0.5">
                          {(Number(inv.cgst) || 0) + (Number(inv.sgst) || 0)}%
                        </p>
                      </div>
                      <div className="bg-emerald-50 p-2.5 rounded-lg border border-emerald-100">
                        <p className="text-[10px] uppercase font-bold text-emerald-700">Grand Total</p>
                        <p className="text-sm font-extrabold text-emerald-800 mt-0.5">
                          ₹{Number(inv.grandTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="bg-gray-50 px-5 py-2.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                    <span>Tax Invoice Generated</span>
                    <button
                      onClick={() => handlePrint(inv)}
                      className="text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 cursor-pointer"
                    >
                      <FaPrint size={12} /> Print PDF &rarr;
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* TABLE LIST VIEW */
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full table-auto border-collapse">
              <thead className="bg-blue-600 text-white uppercase text-xs font-semibold tracking-wider">
                <tr>
                  <th className="p-3.5 text-left">S.No</th>
                  <th className="p-3.5 text-left">Invoice No</th>
                  <th className="p-3.5 text-left">Client / Company</th>
                  <th className="p-3.5 text-left">Billing Period</th>
                  <th className="p-3.5 text-left">Consignments</th>
                  <th className="p-3.5 text-right">Grand Total</th>
                  <th className="p-3.5 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-xs">
                {paginated.map((inv, i) => {
                  const sNo = (currentPage - 1) * pageSize + i + 1;
                  const client = inv.clientId || {};

                  return (
                    <tr key={inv._id} className="hover:bg-blue-50/50 transition">
                      <td className="p-3.5 font-bold text-gray-500">{sNo}</td>
                      <td className="p-3.5 font-extrabold text-blue-700 text-sm">
                        {inv.invoiceNumber}
                      </td>
                      <td className="p-3.5">
                        <span className="font-bold text-gray-900 block">
                          {client.companyName || 'N/A'}
                        </span>
                        <span className="text-[11px] text-gray-500">{client.name || ''}</span>
                      </td>
                      <td className="p-3.5">
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 uppercase">
                          {inv.billMonth}
                        </span>
                      </td>
                      <td className="p-3.5 font-semibold text-gray-700">
                        {inv.noOfConsignment || 0}
                      </td>
                      <td className="p-3.5 text-right font-extrabold text-emerald-600 text-sm">
                        ₹{Number(inv.grandTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="p-3.5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => setViewInvoice(inv)}
                            className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title="View Invoice Details"
                          >
                            <MdVisibility size={18} />
                          </button>
                          <button
                            onClick={() => handlePrint(inv)}
                            className="p-1.5 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition"
                            title="Print Tax Invoice PDF"
                          >
                            <FaPrint size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {filtered.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200 text-xs text-gray-600">
            <div>
              Showing{' '}
              <span className="font-bold text-gray-800">
                {(currentPage - 1) * pageSize + 1}
              </span>{' '}
              to{' '}
              <span className="font-bold text-gray-800">
                {Math.min(currentPage * pageSize, filtered.length)}
              </span>{' '}
              of <span className="font-bold text-gray-800">{filtered.length}</span> entries
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((p, idx, arr) => {
                  const prevVal = arr[idx - 1];
                  return (
                    <React.Fragment key={p}>
                      {prevVal && p - prevVal > 1 && <span className="px-1 text-gray-400">...</span>}
                      <button
                        onClick={() => setCurrentPage(p)}
                        className={`px-3 py-1.5 rounded-lg font-bold transition cursor-pointer ${
                          currentPage === p
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* VIEW INVOICE DETAILS MODAL */}
      {viewInvoice && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white font-bold text-lg">
                  <MdReceipt />
                </div>
                <div>
                  <h3 className="font-extrabold text-lg text-white">
                    Invoice #{viewInvoice.invoiceNumber}
                  </h3>
                  <p className="text-xs text-blue-100">
                    Billing details, taxes, freight calculation & client breakdown
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewInvoice(null)}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition cursor-pointer"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5 text-xs">
              {/* Meta bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                <div>
                  <span className="font-bold text-gray-500 uppercase block text-[10px]">Billing Month</span>
                  <span className="font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded uppercase">
                    {viewInvoice.billMonth}
                  </span>
                </div>
                <div>
                  <span className="font-bold text-gray-500 uppercase block text-[10px]">Invoice Date</span>
                  <span className="font-bold text-gray-800">
                    {viewInvoice.invoiceDate ? new Date(viewInvoice.invoiceDate).toLocaleDateString('en-GB') : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="font-bold text-gray-500 uppercase block text-[10px]">HSN / SAC</span>
                  <span className="font-bold text-gray-800">{viewInvoice.hsnCode || '9968'}</span>
                </div>
                <div>
                  <span className="font-bold text-gray-500 uppercase block text-[10px]">Product</span>
                  <span className="font-bold text-gray-800">{viewInvoice.product || 'DOMESTIC EXPRESS'}</span>
                </div>
              </div>

              {/* Client Info */}
              <div className="p-4 rounded-xl border border-blue-100 bg-blue-50/40">
                <p className="text-xs font-bold text-blue-800 uppercase mb-1">Bill To Client</p>
                <p className="font-extrabold text-gray-900 text-sm">
                  {viewInvoice.clientId?.companyName || 'N/A'}{' '}
                  {viewInvoice.clientId?.name ? `(${viewInvoice.clientId.name})` : ''}
                </p>
                <p className="text-gray-600 mt-1">
                  Address: {formatClientAddress(viewInvoice.clientId)}
                </p>
                <p className="text-gray-600 font-semibold mt-0.5">
                  GSTIN: {viewInvoice.clientId?.gstNumber || 'N/A'}
                </p>
              </div>

              {/* Breakdown */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="font-bold text-gray-700 uppercase mb-3 flex items-center gap-1.5">
                  <HiOutlineCurrencyRupee /> Financial Breakdown
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                    <span className="text-[10px] uppercase font-bold text-gray-500 block">Total Qty</span>
                    <span className="text-base font-extrabold text-gray-800">{viewInvoice.noOfConsignment || 0}</span>
                  </div>
                  <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                    <span className="text-[10px] uppercase font-bold text-gray-500 block">Freight Chrg</span>
                    <span className="text-base font-extrabold text-gray-800">
                      ₹{Number(viewInvoice.frieghtCharge || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                    <span className="text-[10px] uppercase font-bold text-gray-500 block">CGST / SGST</span>
                    <span className="text-base font-extrabold text-gray-800">
                      {viewInvoice.cgst || 0}% / {viewInvoice.sgst || 0}%
                    </span>
                  </div>
                  <div className="bg-emerald-50 p-2.5 rounded-lg border border-emerald-200">
                    <span className="text-[10px] uppercase font-bold text-emerald-700 block">Grand Total</span>
                    <span className="text-base font-extrabold text-emerald-800">
                      ₹{Number(viewInvoice.grandTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              {viewInvoice.tAndC && (
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-gray-600 text-xs">
                  <span className="font-bold text-gray-700 block uppercase text-[10px] mb-0.5">Terms & Conditions:</span>
                  <p className="italic">{viewInvoice.tAndC}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200 rounded-b-2xl">
              <button
                onClick={() => handlePrint(viewInvoice)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold uppercase transition flex items-center gap-1.5 cursor-pointer"
              >
                <FaPrint size={14} /> Print Tax Invoice (PDF)
              </button>
              <button
                onClick={() => setViewInvoice(null)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs font-bold uppercase transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GENERATE INVOICE MODAL */}
      {showGenerateForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 md:p-8 shadow-2xl relative border border-gray-200">
            <button
              onClick={() => setShowGenerateForm(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
            >
              <FaTimes size={20} />
            </button>

            <div className="border-b border-gray-200 pb-3 mb-5">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FaFileInvoice className="text-blue-600" />
                Generate New Invoice
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Generate billing statement for consignments between selected dates
              </p>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Select Client *
                </label>
                <Select
                  placeholder="Search and select client..."
                  options={clients.map(c => ({
                    value: c._id,
                    label: `${c.companyName || ''} - ${c.name || ''}`.trim()
                  }))}
                  value={
                    formData.clientId
                      ? clients
                          .map(c => ({
                            value: c._id,
                            label: `${c.companyName || ''} - ${c.name || ''}`.trim()
                          }))
                          .find(opt => opt.value === formData.clientId) || null
                      : null
                  }
                  onChange={opt => setFormData(prev => ({ ...prev, clientId: opt ? opt.value : '' }))}
                  styles={customSelectStyles}
                  isClearable
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    From Date *
                  </label>
                  <input
                    type="date"
                    className="w-full p-2.5 border border-gray-300 rounded-lg bg-gray-50 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.fromDate}
                    onChange={e => setFormData({ ...formData, fromDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    To Date *
                  </label>
                  <input
                    type="date"
                    className="w-full p-2.5 border border-gray-300 rounded-lg bg-gray-50 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.toDate}
                    onChange={e => setFormData({ ...formData, toDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowGenerateForm(false)}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold text-xs uppercase transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleGenerate}
                  className="px-7 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-xs uppercase shadow-md transition cursor-pointer"
                >
                  Generate Statement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoice;